import { Colli } from '@frontend/colli/types';
import { Package, PackageType } from '@frontend/package/types';

import { fetchOpenableDropOffPackages, isOpenableDropOffPackage } from './seed-drop-off-colli-context';
import { fetchOpenablePickUpPackages, isOpenablePickUpPackage } from './seed-pick-up-colli-context';

export { isOpenableDropOffPackage, isOpenablePickUpPackage };

export const COMPLETED_OPEN_SLOT_PACKAGE_IDS_KEY = 'completed_open_slot_package_ids';
export const SERIAL_CONFIRM_OPEN_SLOTS_SCREEN_KEY = 'serial_confirm_open_slots_screen';

export function isOpenablePackageForType(pkg: Package, type: PackageType.DROP_OFF | PackageType.PICK_UP): boolean {
    return type === PackageType.DROP_OFF ? isOpenableDropOffPackage(pkg) : isOpenablePickUpPackage(pkg);
}

/** Prefer `packages[]`; fall back to singular wizard `package` from pin/QR identify. */
export function getSeededPackagesFromWizardData(data: Record<string, unknown> | undefined | null): Package[] | null {
    const packages = data?.packages as Package[] | undefined;
    if (packages?.length) return packages;
    const singular = data?.package as Package | undefined;
    return singular ? [singular] : null;
}

function filterByCollis(packages: Package[], collis?: Colli[] | null): Package[] {
    if (!collis?.length) return packages;
    const colliIds = new Set(collis.map((c) => c.id));
    return packages.filter((pkg) => pkg.colli_id != null && colliIds.has(pkg.colli_id));
}

function sortByPriority(packages: Package[]): Package[] {
    return [...packages].sort((a, b) => a.priority - b.priority);
}

/**
 * Resolve openable packages for the given collis (or all openable for the txn at SPOT).
 * Prefers an already-seeded packages list when it matches the type.
 */
export async function resolveOpenablePackagesForCollis(args: {
    accountId: string;
    transactionId: string;
    spotId?: string | null;
    collis?: Colli[] | null;
    type: PackageType.DROP_OFF | PackageType.PICK_UP;
    seededPackages?: Package[] | null;
}): Promise<Package[]> {
    const colliIds = args.collis?.length ? args.collis.map((c) => c.id) : undefined;

    if (args.seededPackages?.length) {
        const seeded = sortByPriority(
            filterByCollis(
                args.seededPackages.filter((pkg) => isOpenablePackageForType(pkg, args.type)),
                args.collis
            )
        );
        if (seeded.length > 0) return seeded;
    }

    if (args.type === PackageType.DROP_OFF) {
        return fetchOpenableDropOffPackages({
            accountId: args.accountId,
            transactionId: args.transactionId,
            spotId: args.spotId,
            colliIds
        });
    }

    return fetchOpenablePickUpPackages({
        accountId: args.accountId,
        transactionId: args.transactionId,
        spotId: args.spotId,
        colliIds
    });
}

export function getCompletedOpenSlotPackageIds(data: Record<string, unknown> | undefined | null): string[] {
    const ids = data?.[COMPLETED_OPEN_SLOT_PACKAGE_IDS_KEY];
    return Array.isArray(ids) ? (ids as string[]) : [];
}

export function getNextSerialPackage(packages: Package[], completedIds: string[]): Package | null {
    const completed = new Set(completedIds);
    return sortByPriority(packages).find((pkg) => !completed.has(pkg.id)) ?? null;
}

export interface SerialProgress {
    index: number;
    total: number;
    colli: Colli | null;
}

export function getSerialProgress(packages: Package[], completedIds: string[], current: Package | null, collis?: Colli[] | null): SerialProgress {
    const ordered = sortByPriority(packages);
    const total = ordered.length;
    if (!current || total === 0) {
        return { index: 0, total, colli: null };
    }
    const position = ordered.findIndex((pkg) => pkg.id === current.id);
    const index = position >= 0 ? position + 1 : completedIds.length + 1;
    const colli = collis?.find((c) => c.id === current.colli_id) ?? null;
    return { index, total, colli };
}

export function packageSlotIds(packages: Package[]): string[] {
    return [...new Set(packages.map((pkg) => pkg.slot_id).filter((id): id is string => !!id))];
}
